import React, { useCallback, useEffect, useState } from "react";
import api, { API_URL, formatRupiah, getToken, SPP_TAHUN , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ORTU } from "@/lib/nav";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

const BULAN_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const BULAN_KEYS = ["01","02","03","04","05","06","07","08","09","10","11","12"];

export function RincianTabunganOrtu() {
  const [tahun, setTahun] = useState(SPP_TAHUN[0]);
  const [dataType, setDataType] = useState("perhari");
  const [applied, setApplied] = useState({ tahun: SPP_TAHUN[0], dataType: "perhari" });
  const [payload, setPayload] = useState(null); // {view, data}
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setPayload(null);
    try {
      if (applied.dataType === "perhari") {
        const { data } = await api.get("/tabungan/rincian/ortu-perhari", { params: { tahun: applied.tahun } });
        setPayload({ view: "perhari", data });
      } else {
        const { data } = await api.get("/tabungan/rincian/ortu-tahunan", { params: { tahun: applied.tahun } });
        setPayload({ view: "tahunan", data });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied({ tahun, dataType });
  const resetFilter = () => { setTahun(SPP_TAHUN[0]); setDataType("perhari"); setApplied({ tahun: SPP_TAHUN[0], dataType: "perhari" }); };

  const downloadHref = () => {
    const t = getToken();
    const params = new URLSearchParams({ tahun: applied.tahun, view: applied.dataType });
    if (t) params.append("auth", t);
    return `${API_URL}/tabungan/download/ortu/rincian?${params.toString()}`;
  };

  return (
    <Layout nav={NAV_ORTU}>
      <PageTitle title="Rincian Tabungan" action={
        <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-primary" data-testid="ortu-tab-download">
          <Download className="w-4 h-4" /> Download
        </a>
      } />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="pp-label">Tahun</label>
            <select className="pp-input" value={tahun} onChange={(e) => setTahun(parseInt(e.target.value, 10))} data-testid="ortu-tab-tahun">
              {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="pp-label">Data</label>
            <select className="pp-input" value={dataType} onChange={(e) => setDataType(e.target.value)} data-testid="ortu-tab-data">
              <option value="perhari">Rekap Perhari</option>
              <option value="tahunan">Rekap Tahunan</option>
            </select>
          </div>
          <div className="flex items-end gap-2 sm:col-span-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="ortu-tab-apply">Terapkan</button>
            <button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="ortu-tab-reset">Reset</button>
          </div>
        </div>
      </section>

      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}
      {!loading && payload?.view === "perhari" && <PerhariTable data={payload.data} />}
      {!loading && payload?.view === "tahunan" && <TahunanTable data={payload.data} />}
    </Layout>
  );
}

function PerhariTable({ data }) {
  return (
    <section className="pp-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full text-xs" data-testid="ortu-tab-perhari-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-3 py-2">Tgl</th>
            {BULAN_SHORT.map((b) => <th key={b} className="text-right px-2 py-2">{b}</th>)}
            <th className="text-right px-3 py-2">TOTAL</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.tanggal} className="pp-row">
                <td className="px-3 py-2 font-medium">{r.tanggal}</td>
                {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{r.per_bulan[b] > 0 ? formatRupiah(r.per_bulan[b]).replace("Rp ", "") : "—"}</td>)}
                <td className="px-3 py-2 text-right font-mono font-semibold">{r.total > 0 ? formatRupiah(r.total) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50 font-bold">
              <td className="px-3 py-2">TOTAL</td>
              {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{data.col_totals[b] > 0 ? formatRupiah(data.col_totals[b]).replace("Rp ", "") : "—"}</td>)}
              <td className="px-3 py-2 text-right font-mono">{formatRupiah(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function TahunanTable({ data }) {
  return (
    <section className="pp-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full text-sm" data-testid="ortu-tab-tahunan-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-3 py-2 min-w-[160px]">Nama Siswa</th>
            {BULAN_SHORT.map((b) => <th key={b} className="text-right px-2 py-2">{b}</th>)}
            <th className="text-right px-3 py-2">JUMLAH TABUNGAN</th>
          </tr></thead>
          <tbody>
            <tr className="pp-row">
              <td className="px-3 py-2 font-medium">{data.siswa.nama}</td>
              {BULAN_KEYS.map((b) => <td key={b} className="px-2 py-2 text-right font-mono">{data.per_bulan[b] > 0 ? formatRupiah(data.per_bulan[b]) : "—"}</td>)}
              <td className="px-3 py-2 text-right font-mono font-semibold">{formatRupiah(data.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SaldoTabunganOrtu() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/tabungan/saldo/ortu"); setData(data); }
      catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
      finally { setLoading(false); }
    })();
  }, []);
  const downloadHref = () => {
    const t = getToken();
    return `${API_URL}/tabungan/download/ortu/saldo${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  };
  return (
    <Layout nav={NAV_ORTU}>
      <PageTitle title="Saldo Tabungan" action={
        <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-primary" data-testid="ortu-saldo-download">
          <Download className="w-4 h-4" /> Download
        </a>
      } />
      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}
      {!loading && data && (
        <section className="pp-card overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-xs uppercase text-slate-500">Nama Siswa</div>
              <div className="font-semibold text-lg">{data.siswa.nama} <span className="text-xs text-slate-400 font-mono">({data.siswa.nis || "—"})</span></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase text-slate-500">Saldo Akhir</div>
              <div className="font-bold text-2xl text-indigo-700">{formatRupiah(data.saldo_akhir)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ortu-saldo-table">
              <thead><tr className="pp-table-header">
                <th className="text-left px-4 py-3">Tahun</th>
                <th className="text-right px-4 py-3">Jumlah</th>
                <th className="text-left px-4 py-3">Tgl Penarikan</th>
                <th className="text-right px-4 py-3">Jml Penarikan</th>
                <th className="text-left px-4 py-3">Keterangan</th>
                <th className="text-right px-4 py-3">Debit</th>
                <th className="text-right px-4 py-3">Kredit</th>
                <th className="text-right px-4 py-3">Saldo</th>
                <th className="text-left px-4 py-3">Bukti</th>
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400 italic">Belum ada aktivitas tabungan.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.tahun} className="pp-row">
                    <td className="px-4 py-3 font-medium">{r.tahun}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.setoran ? formatRupiah(r.setoran) : "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => p.tanggal).join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => formatRupiah(p.jumlah)).join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-pre-line">{r.penarikan.length ? r.penarikan.map((p) => p.keterangan || "—").join("\n") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">{r.debit ? formatRupiah(r.debit) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-rose-700">{r.kredit ? formatRupiah(r.kredit) : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{formatRupiah(r.saldo)}</td>
                    <td className="px-4 py-3">
                      {r.penarikan.length === 0 ? "—" : (
                        <div className="flex flex-col gap-1">
                          {r.penarikan.map((p) => {
                            const t = getToken();
                            const href = `${API_URL}/tabungan/penarikan/${p.id}/kwitansi.pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
                            return (
                              <a key={p.id} href={href} target="_blank" rel="noreferrer" className="text-red-600 inline-flex items-center gap-1 text-xs" data-testid={`ortu-tab-kwitansi-${p.id}`}>
                                <FileText className="w-3 h-3" /> Kwitansi
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </Layout>
  );
}
